import { Service } from '../../../store/service/types';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import UpTransition from '../../../shared/UpTransition/UpTransition';
import { createIconFromName } from '../../../util/iconUtil';
import { useEffect, useState } from 'react';
import parameterSelector, { SelectedValueType } from './parameter/parameterSelector';
import useComplexObject from './parameter/useComplexObject';
import ErrorTypography from '../../../shared/ErrorTypography/ErrorTypography';
import { ParameterValidatorService } from './parameter/parameterValidatorService';
import { Trans, useTranslation } from 'react-i18next';
import { Graph as GraphType } from '../../../store/data/types';
import { nameTranslator } from '../../../locales/NameTranslator';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { togglePauseTut } from '../../../store/tutorial/tutorialSlice';

type Props = {
  service: Service | null;
  graphInfo: GraphType | null;
  onClose: (success: boolean, name: string, data: Record<string, SelectedValueType>) => void;
};

const AddServiceDialog = ({ service, graphInfo, onClose }: Props): JSX.Element => {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [parameters, updateParameters, resetParameters] = useComplexObject<SelectedValueType>({});
  const [error, setError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const dispatch = useAppDispatch();
  const tutorial = useAppSelector(state => state.tutorial);

  const reset = () => {
    resetParameters();
    setName('');
    setError(false);
    setNameError(false);
  };



  const array = graphInfo?.nodes;
  const servicesNames = array?.map(node => node.title);
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reset, [service]);

  const handleClose = (success: boolean) => () => {
    if (success) {
      if(tutorial.active) {
        if(tutorial.paused && tutorial.actionSteps.includes(tutorial.stepIndex)) dispatch(togglePauseTut());
      }
      const validationResult = name && ParameterValidatorService.validateAll(service?.parameters ?? [], parameters);
      if (!validationResult) {
        setError(true);
        return;
      }
      if (servicesNames) {
        if (servicesNames.filter(ServiceName => ServiceName===name).length > 0) {
          setNameError(true);
          return;
        }
      }
    }
    onClose(success, name, parameters);
    reset();
  };

  const handleParameterChange = (parameter: string) => (value: SelectedValueType) => updateParameters(parameter, value);

  return (
    <Dialog
      open={service !== null}
      onClose={handleClose(false)}
      aria-labelledby={'add-service-dialog-title'}
      aria-describedby={'add-service-dialog-description'}
      fullWidth={true}
      maxWidth={'md'}
      TransitionComponent={UpTransition}
      disableEnforceFocus
    >
      {service !== null && (
        <div className='addServiceDialog'>
          <DialogTitle id={'add-service-dialog-title'}>
            {createIconFromName(service.icon)} Service <i>{nameTranslator(service)}</i> hinzufügen
          </DialogTitle>
          <DialogContent>
            <TextField
              variant={'filled'}
              label={t('Name')}
              value={name}
              onChange={event => setName(event.target.value)}
              fullWidth
            />
            {service.parameters.length > 0 && (
              <>
                <Typography variant={'h6'} sx={{ my: 3 }}>
                  <Trans>Parameter</Trans>
                </Typography>
                <Stack spacing={1}>
                  {service.parameters.map((parameter, i) => {
                    const Component = parameterSelector(parameter.type);
                    return (
                      <Component
                        key={i}
                        {...parameter}
                        onChange={handleParameterChange(parameter.parameter)}
                        selected={parameters[parameter.parameter] ?? undefined}
                      />
                    );
                  })}
                </Stack>
              </>
            )}
            <Typography variant={'h6'} sx={{ my: 3 }}>
              <Trans>Expected Inputs</Trans>&nbsp;{service.inputs}
            </Typography>
            {nameError && <ErrorTypography message={'Diese Name ist schon benutzt'} />}
            {error && <ErrorTypography message={'Bitte Angaben überprüfen'} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleClose(true)}>
              <Trans>Create Service</Trans>
            </Button>
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
};

export default AddServiceDialog;
